/**
 * PostHog Analytics Helper
 * 
 * Provides utility functions for tracking events and managing analytics
 * throughout the application.
 */

import posthog from 'posthog-js';
import { EXCLUDED_ROLES_FROM_POSTHOG_TRACKING, AnalyticsEvents } from 'constants';
import { reportFrontendError } from 'lib/helpers/frontend-error-reporter';

let posthogInitialized = false;

// Deduplicate view events across remounts (desktop + mobile both mount the same form)
const trackedViewEvents = new Set();

const trackViewEventOnce = (dedupeKey, eventName, properties) => {
  if (!dedupeKey || trackedViewEvents.has(dedupeKey)) return;
  trackedViewEvents.add(dedupeKey);
  trackEvent(eventName, properties);
};

export const isPostHogDisabled = () => process.env.NEXT_PUBLIC_APP_ENV !== 'prod';

/**
 * Check if user should be tracked based on their role
 * @param {object} userData - User data object { type: 'admin' | 'candidate' | 'hiring_manager', user: {...} }
 * @returns {boolean} - Whether the user should be tracked
 */
export const shouldTrackUser = (userData) => {
  if (!userData) return false;
  
  // Candidates and hiring managers are always tracked
  if (userData?.type === 'candidate' || userData?.type === 'hiring_manager') return true;
  
  // For admin users, check roles
  const user = userData?.user;
  if (!user?.roles || !Array.isArray(user?.roles)) return true;
  
  // Skip tracking if user has any excluded role
  return !user?.roles?.some(role => EXCLUDED_ROLES_FROM_POSTHOG_TRACKING.includes(role?.name));
};

/**
 * Extract user data from pageProps
 * @param {object} pageProps - Next.js page props
 * @returns {object|null} - User data object or null
 */
export const getUserData = (pageProps) => {
  // Admin users have currentUserDetails
  if (pageProps?.currentUserDetails?.id) {
    return {
      type: 'admin',
      user: pageProps.currentUserDetails
    };
  }
  // Candidates have candidate object
  if (pageProps?.candidate?.id) {
    return {
      type: 'candidate',
      user: pageProps.candidate
    };
  }
  return null;
};

/**
 * Initialize PostHog with configuration
 * @returns {boolean} - Whether initialization was successful
 */
export const initializePostHog = () => {
  if (isPostHogDisabled()) {
    return false;
  }

  if (typeof window === 'undefined') {
    console.error('PostHog is not initialized because window is undefined');
    return false;
  }

  if (!process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN) {
    console.error('PostHog project token is not set');
    reportFrontendError(
      'PostHog',
      'PostHog project token is not defined',
      {
        message: 'NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN is missing',
        status: 0,
      },
      {
        operation: 'Initialize',
        environment: process.env.NEXT_PUBLIC_APP_ENV,
      }
    );
    return false;
  }

  if (posthogInitialized || posthog?.__loaded) {
    posthogInitialized = true;
    return true;
  }

  try {
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN, {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
      person_profiles: 'identified_only',
      capture_pageview: true,
      capture_pageleave: true,
    });
    posthogInitialized = true;
    return true;
  } catch (error) {
    console.error('PostHog initialization error:', error);
    reportFrontendError(
      'PostHog',
      'PostHog initialization failed',
      error,
      {
        operation: 'Initialize',
        environment: process.env.NEXT_PUBLIC_APP_ENV,
      }
    );
    return false;
  }
};

/**
 * Ensure PostHog is initialized and the user is identified before tracking.
 * Safe to call multiple times (e.g. CSR flows without pageProps).
 * @param {object} userData - User data object { type, user }
 * @param {object} context - Extra context (pageProps / companyId for identify)
 * @returns {boolean} - Whether tracking is allowed and ready
 */
export const ensurePostHogReady = (userData, context = {}) => {
  if (!userData || !shouldTrackUser(userData)) return false;
  if (!initializePostHog()) return false;

  identifyUser(userData, context);
  return true;
};

/**
 * Identify and set up user in PostHog
 * @param {object} userData - User data object { type: 'admin' | 'candidate' | 'hiring_manager', user: {...} }
 * @param {object} pageProps - Next.js page props or HM context (for company data)
 */
export const identifyUser = (userData, pageProps = {}) => {
  if (!userData || !shouldTrackUser(userData)) return;

  const { type, user } = userData;

  try {
    if (type === 'admin') {
      // Admin user identification
      const userCompanies = user?.company || [];
      const primaryRole = user?.roles?.[0]?.name || 'Unknown';

      posthog.identify(user?.id, {
        email: user?.email,
        name: user?.name,
        user_type: 'admin',
        role: primaryRole,
        roles: user?.roles?.map(r => r?.name).join(', '),
        company_name: userCompanies?.[0]?.name,
        company_count: userCompanies?.length,
      });

      posthog.register({
        user_id: user?.id,
        user_email: user?.email,
        user_type: 'admin',
        user_role: primaryRole,
        user_roles: user?.roles?.map(r => r?.name).join(', '),
        environment: process.env.NEXT_PUBLIC_APP_ENV,
      });

      // Set up group analytics for admin users
      if (userCompanies?.length > 0) {
        const primaryCompany = userCompanies?.[0];
        posthog.group('account', primaryCompany?.id, {
          name: primaryCompany?.name,
          slug: primaryCompany?.slug,
          type: 'company',
        });
      }

    } else if (type === 'hiring_manager') {
      const companyId = pageProps?.companyId;

      posthog.identify(user?.id, {
        email: user?.email,
        name: user?.name,
        user_type: 'hiring_manager',
        company_id: companyId,
      });

      posthog.register({
        user_id: user?.id,
        user_email: user?.email,
        user_type: 'hiring_manager',
        environment: process.env.NEXT_PUBLIC_APP_ENV,
      });

      if (companyId) {
        posthog.group('account', companyId, {
          type: 'company',
        });
      }

    } else {
      // Candidate user identification
      const company = pageProps?.company || user?.company;
      
      posthog.identify(user?.id, {
        email: user?.emailId,
        name: user?.name,
        user_type: 'candidate',
        candidate_status: user?.status,
        company_name: company?.companyName || company?.name,
        company_id: company?.id,
        position_id: user?.positionId,
        estimated_joining_date: user?.estimatedJoiningDate,
      });

      posthog.register({
        user_id: user?.id,
        user_type: 'candidate',
        environment: process.env.NEXT_PUBLIC_APP_ENV,
      });

      // Set up group analytics for candidate
      if (company?.id) {
        posthog.group('account', company?.id, {
          name: company?.companyName || company?.name,
          slug: company?.slug,
          type: 'company',
        });
      }

    }
  } catch (error) {
    console.error('PostHog identification error:', error);
  }
};

/**
 * Update PostHog group when company switches (for admin users)
 * @param {object} userData - User data object { type: 'admin', user: {...} }
 * @param {string} companySlug - Current company slug from URL
 * @param {string} currentCompanyId - Current company ID from storage
 */
export const updateCompanyGroup = (userData, companySlug, currentCompanyId) => {
  if (!userData || userData?.type !== 'admin') return;
  if (!shouldTrackUser(userData)) return;

  const user = userData?.user;
  const userCompanies = user?.company || [];
  
  if (!companySlug || userCompanies?.length === 0) return;

  const isCurrentCompanySelected = userCompanies?.some(
    (company) => company?.slug === companySlug && company?.id === currentCompanyId
  );
  if (isCurrentCompanySelected) return;

  const currentCompany = userCompanies?.find((company) => company?.slug === companySlug);
  if (!currentCompany?.id) return;

  try {
    posthog.group('account', currentCompany?.id, {
      name: currentCompany?.name,
      slug: currentCompany?.slug,
      type: 'company',
    });

    return currentCompany;
  } catch (error) {
    console.error('PostHog group switch error:', error);
    return null;
  }
};

/**
 * Track a custom event
 * @param {string} eventName - Name of the event
 * @param {object} properties - Event properties
 */
export const trackEvent = (eventName, properties = {}) => {
  if (typeof window === 'undefined' || isPostHogDisabled()) return;
  
  try {
    posthog?.capture(eventName, properties);
  } catch (error) {
    console.error('PostHog tracking error:', error);
    reportFrontendError(
      'PostHog',
      'PostHog tracking error',
      error,
      {
        operation: 'TrackEvent',
        environment: process.env.NEXT_PUBLIC_APP_ENV,
      }
    );
  }
};

/**
 * Helper functions for common tracking events
 */

/**
 * Track search performed
 * @param {object} params - Search parameters
 */
export const trackSearch = ({ searchQuery, searchType, resultsCount, source }) => {
  trackEvent(AnalyticsEvents.SEARCH_PERFORMED, {
    search_query: searchQuery,
    search_type: searchType,
    results_count: resultsCount,
    source,
  });
};

/**
 * Track candidate profile view
 * @param {object} params - Profile view parameters
 */
export const trackCandidateProfileView = ({ candidateId, source }) => {
  trackEvent(AnalyticsEvents.CANDIDATE_PROFILE_VIEWED, {
    candidate_id: candidateId,
    source,
  });
};

/**
 * Track dashboard filter click (hot/warm/cold)
 * @param {object} params - Dashboard filter parameters
 */
export const trackDashboardFilterClick = ({ filterType, count }) => {
  const eventMap = {
    hot: AnalyticsEvents.DASHBOARD_HOT_CLICKED,
    warm: AnalyticsEvents.DASHBOARD_WARM_CLICKED,
    cold: AnalyticsEvents.DASHBOARD_COLD_CLICKED,
  };
  
  trackEvent(eventMap?.[filterType], {
    filter_type: filterType,
    count,
  });
};

/**
 * Track call initiated
 * @param {object} params - Call parameters
 */
export const trackCallInitiated = ({ candidateId, source }) => {
  trackEvent(AnalyticsEvents.CALL_INITIATED, {
    candidate_id: candidateId,
    source,
  });
};

/**
 * Track intervention submitted (Add Intervention / Add Recruiter Sentiment modal)
 * @param {object} params - Intervention parameters
 */
export const trackInterventionSubmitted = ({ candidateId, recruiterSentiment, source }) => {
  trackEvent(AnalyticsEvents.INTERVENTION_SUBMITTED, {
    candidate_id: candidateId,
    recruiter_sentiment: recruiterSentiment,
    source,
  });
};

/**
 * Track quiz viewed
 * @param {object} params - Quiz parameters
 */
export const trackQuizViewed = ({ quizId, touchpointId }) => {
  trackViewEventOnce(
    `quiz_viewed:${touchpointId || quizId}`,
    AnalyticsEvents.QUIZ_VIEWED,
    {
      quiz_id: quizId,
      touchpoint_id: touchpointId,
    }
  );
};

/**
 * Track survey viewed
 * @param {object} params - Survey parameters
 */
export const trackSurveyViewed = ({ surveyId, touchpointId }) => {
  trackViewEventOnce(
    `survey_viewed:${touchpointId || surveyId}`,
    AnalyticsEvents.SURVEY_VIEWED,
    {
      survey_id: surveyId,
      touchpoint_id: touchpointId,
    }
  );
};

/**
 * Track candidate hub viewed
 * @param {object} params - Candidate hub parameters
 */
export const trackCandidateHubViewed = ({ candidateId, daysToDoj, hasPendingTasks }) => {
  trackEvent(AnalyticsEvents.CANDIDATE_HUB_VIEWED, {
    candidate_id: candidateId,
    days_to_doj: daysToDoj,
    has_pending_tasks: hasPendingTasks,
  });
};

/**
 * Track knowledge hub or help widget click
 * @param {object} params - Widget parameters
 */
export const trackHelpWidgetClick = ({ widgetType, currentPage }) => {
  const eventMap = {
    knowledge_hub: AnalyticsEvents.KNOWLEDGE_HUB_CLICKED,
    help_widget: AnalyticsEvents.HELP_WIDGET_CLICKED,
  };
  
  trackEvent(eventMap?.[widgetType], {
    widget_type: widgetType,
    current_page: currentPage,
  });
};

/**
 * Track hiring manager page viewed
 * @param {object} params - HM page parameters
 */
export const trackHiringManagerPortalDashboardViewed = ({ hiringManagerUserId, companyId }) => {
  trackEvent(AnalyticsEvents.HIRING_MANAGER_PORTAL_DASHBOARD_VIEWED, {
    hiring_manager_user_id: hiringManagerUserId,
    company_id: companyId,
  });
};

/**
 * Track hiring manager portal help & tutorials click
 * @param {object} params - Help click parameters
 */
export const trackHiringManagerPortalHelpClicked = ({ email }) => {
  trackEvent(AnalyticsEvents.HIRING_MANAGER_PORTAL_HELP_CLICKED, {
    email,
  });
};

/**
 * Track user login
 * @param {object} params - Login parameters
 */
export const trackUserLogin = ({ userId, userRole, loginMethod }) => {
  trackEvent(AnalyticsEvents.USER_LOGGED_IN, {
    user_id: userId,
    user_role: userRole,
    login_method: loginMethod,
  });
};
